"use client";

import { useMemo, useState } from "react";
import { ArrowRightLeft, Users } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { useToast } from "@/app/_module/hooks/use-toast";

import { useGetSubAgencies, useMoveUsersBulk } from "../../hooks/use-hook";
import type { SubAgency } from "../../types/types";
import { bulkMoveToast } from "./bulkMoveToast";

interface BulkMoveAgentsModalProps {
  open: boolean;
  onClose: () => void;
  sourceAgencyId: string;
  sourceAgencyName: string;
  selectedUserIds: string[];
  onSuccess: () => void;
  /** Agency whose subtree (and itself) are the target candidates. Defaults to
   *  the source. Pass the PARENT here (e.g. from Sub-Agency Details) so agents
   *  can be moved up to the parent or across to siblings. */
  targetTreeRootId?: string;
  targetTreeRootName?: string;
}

interface AgencyOption {
  id: string;
  name: string;
  parentAgencyId: string;
  depth: number;
}

export const BulkMoveAgentsModal = function ({
  open,
  onClose,
  sourceAgencyId,
  sourceAgencyName,
  selectedUserIds,
  onSuccess,
  targetTreeRootId,
  targetTreeRootName,
}: BulkMoveAgentsModalProps) {
  const [targetId, setTargetId] = useState<string | null>(null);
  const { toast } = useToast();
  const bulkMove = useMoveUsersBulk();

  const rootId = targetTreeRootId || sourceAgencyId;
  const { data: subAgenciesData, isLoading } = useGetSubAgencies({
    parentAgencyId: rootId,
    page: 1,
    limit: 200,
  });

  const normalize = (input: unknown): SubAgency[] => {
    if (Array.isArray(input)) return input.filter(Boolean) as SubAgency[];
    if (input && typeof input === "object") {
      const obj = input as Record<string, unknown>;
      if (Array.isArray(obj.items)) return obj.items.filter(Boolean) as SubAgency[];
      if (obj.data && typeof obj.data === "object") {
        const d = obj.data as Record<string, unknown>;
        if (Array.isArray(d.items)) return d.items.filter(Boolean) as SubAgency[];
      }
    }
    return [];
  };

  const options = useMemo(() => {
    const all = normalize(subAgenciesData).filter((a) => a.isActive);
    const childrenMap = new Map<string, SubAgency[]>();
    for (const a of all) {
      if (!childrenMap.has(a.parentAgencyId)) childrenMap.set(a.parentAgencyId, []);
      childrenMap.get(a.parentAgencyId)!.push(a);
    }
    const flat: AgencyOption[] = [];
    const traverse = (parentId: string, depth: number) => {
      for (const child of childrenMap.get(parentId) || []) {
        flat.push({ id: child.id, name: child.name, parentAgencyId: child.parentAgencyId, depth });
        traverse(child.id, depth + 1);
      }
    };
    traverse(rootId, 0);
    // When moving from a sub-agency, also offer its PARENT (the tree root) as a
    // target so agents can be moved back up.
    const withRoot =
      rootId !== sourceAgencyId
        ? [{ id: rootId, name: `${targetTreeRootName || "Parent agency"} (top)`, parentAgencyId: "", depth: 0 }, ...flat]
        : flat;
    // Never offer the source agency itself as a destination.
    return withRoot.filter((o) => o.id !== sourceAgencyId);
  }, [subAgenciesData, sourceAgencyId, rootId, targetTreeRootName]);

  const handleSubmit = async () => {
    if (!targetId || selectedUserIds.length === 0) return;
    try {
      const res = await bulkMove.mutateAsync({
        sourceAgencyId,
        targetAgencyId: targetId,
        userIds: selectedUserIds,
      });
      const data = (res as any)?.data ?? res;
      toast(bulkMoveToast(data, selectedUserIds.length));
      onSuccess();
      setTargetId(null);
      onClose();
    } catch (err: any) {
      toast({
        title: "Could not move agents",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md z-[70]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-primary" />
            Move {selectedUserIds.length} Agent{selectedUserIds.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Move the selected agent{selectedUserIds.length === 1 ? "" : "s"} out of{" "}
            <span className="font-medium text-foreground">{sourceAgencyName}</span> into a sub-agency. All their
            leads, history, and settings move with them.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            {selectedUserIds.length} agent{selectedUserIds.length === 1 ? "" : "s"} selected
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Move into</p>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              value={targetId ?? ""}
              onChange={(e) => setTargetId(e.target.value || null)}
              disabled={isLoading}
            >
              <option value="">{isLoading ? "Loading sub-agencies…" : "Select a sub-agency…"}</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {" ".repeat(o.depth * 2)}
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={bulkMove.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!targetId || bulkMove.isPending}>
            {bulkMove.isPending ? "Moving…" : `Move ${selectedUserIds.length} agent${selectedUserIds.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkMoveAgentsModal;
